import { useEffect, useState } from "react";

import { BUCKET_END_WARNING_TEXT, bucketEndNotificationTag, bucketEndWarningDelayMs } from "@/lib/buckets/bucketEndWarning";
import { useUIStore } from "@/stores/ui";

import { bucketStatus } from "@workspace/schemas/drizzle/buckets";
import type { Bucket } from "@workspace/schemas/zod";

export function useBucketEndWarning(bucket: Pick<Bucket, "id" | "status" | "plannedEndTime"> | undefined) {
  const [firedEndMs, setFiredEndMs] = useState<number | null>(null);
  // Primitive deps: the bucket object is new after every refetch and would restart the timer.
  const bucketId = bucket?.id;
  const status = bucket?.status;
  const plannedEndMs = bucket?.plannedEndTime.getTime();

  useEffect(() => {
    if (bucketId === undefined || status === undefined || plannedEndMs === undefined) return;
    const rule = { status, plannedEndTime: new Date(plannedEndMs) };
    const delay = bucketEndWarningDelayMs(rule);
    if (delay === null) return;

    const fire = () => {
      if (useUIStore.getState().dismissedBucketEndWarnings[bucketId] === plannedEndMs) return;
      setFiredEndMs(plannedEndMs);
      if (document.visibilityState === "hidden" && typeof Notification !== "undefined" && Notification.permission === "granted") {
        try {
          const notification = new Notification(BUCKET_END_WARNING_TEXT.TITLE, {
            body: BUCKET_END_WARNING_TEXT.BODY,
            tag: bucketEndNotificationTag(bucketId),
          });
          notification.onclick = () => window.focus();
        } catch {
          // Chrome on Android throws here.
        }
      }
    };

    const timeoutId = setTimeout(fire, delay);
    // A sleeping laptop or a frozen tab runs no timers; re-check when the tab comes back.
    const onVisible = () => {
      if (document.visibilityState === "visible" && bucketEndWarningDelayMs(rule) === 0) fire();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [bucketId, status, plannedEndMs]);

  const dismiss = () => {
    if (bucketId !== undefined && firedEndMs !== null) useUIStore.getState().dismissBucketEndWarning(bucketId, firedEndMs);
    setFiredEndMs(null);
  };

  const open = firedEndMs !== null && firedEndMs === plannedEndMs && status === bucketStatus.IN_PROGRESS;
  return { open, dismiss };
}
